import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider, useDispatch } from 'react-redux';
import filter from 'lodash/filter';

import { Button, Stack } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';

import api from 'api';
import { downloadFile, downloadDataFile } from 'store/actions/files';

import store from 'components/store';
import Modal from 'components/elements/BasicModal';

interface FileValue {
  content: string;
  name: string;
}

interface DisplayOptionsProps {
  values: FileValue[];
  onFileDelete: (id: string) => void;
  isEditMode: boolean;
}

const isDataFile = (fileId: string) => fileId.startsWith('data');

const DisplayOptions: React.FC<DisplayOptionsProps> = ({
  values,
  onFileDelete,
  isEditMode,
}) => {
  const dispatch = useDispatch<any>();
  const [toDelete, setToDelete] = useState<{ id: string; name: string } | null>(null);

  const handleFileDownload = (id: string, fileName: string) => {
    if (isDataFile(id)) {
      downloadDataFile({ data: id, fileName });
    } else {
      dispatch(downloadFile({ id, fileName }));
    }
  };

  const handleFileDelete = (id: string) => {
    if (isDataFile(id)) onFileDelete(id);
    else {
      api.deleteFile(id).then(() => onFileDelete(id));
    }
  };

  const handleFileDeleteConfirm = (id: string, name: string) => {
    setToDelete({ id, name });
  };

  return (
    <div>
      {toDelete && (
        <Modal
          title={`Are you sure you want to remove "${toDelete.name}"?`}
          isOpen
          onClose={() => setToDelete(null)}
          onConfirm={() => {
            setToDelete(null);
            handleFileDelete(toDelete.id);
          }}
        />
      )}
      <Stack>
        {values.map(({ content, name }) => (
          <div key={name}>
            <Button variant="link" onClick={() => handleFileDownload(content, name)}>
              {name}
            </Button>
            {isEditMode && (
              <Button
                variant="danger"
                onClick={() => handleFileDeleteConfirm(content, name)}
                className="rounded-circle"
                size="sm"
              >
                <FaTimes />
              </Button>
            )}
          </div>
        ))}
      </Stack>
    </div>
  );
};

interface UpdatePreviewParams {
  container: HTMLElement;
  onFileDelete: (id: string) => void;
  values: FileValue[];
  isEditMode: boolean;
}

const updatePreview = ({
  container,
  onFileDelete,
  values,
  isEditMode,
}: UpdatePreviewParams) => {
  if (!values) return;
  const root = ReactDOM.createRoot(container);
  root.render(
    <Provider store={store}>
      <DisplayOptions values={values} onFileDelete={onFileDelete} isEditMode={isEditMode} />
    </Provider>,
  );
};

interface SurveyFileQuestionOverrideOptions {
  question: {
    value: FileValue[];
    getType: () => string;
    onPropertyChanged: {
      add: (callback: (question: any, opt: {
        name: string;
        newValue: FileValue[];
      }) => void) => void;
    };
  };
  htmlElement: HTMLElement;
}

export default function surveyFileQuestionOverride(
  { isEditMode }: { isEditMode: boolean },
  options: SurveyFileQuestionOverrideOptions,
) {
  const onFileDelete = (id: string) => {
    // eslint-disable-next-line no-param-reassign
    options.question.value = filter(options.question.value, ({ content }) => content !== id);
  };

  if (options.question.getType() === 'file') {
    const container = document.createElement('div');
    const fileElement = options.htmlElement.getElementsByClassName('sv-file__decorator')[0];
    fileElement.parentNode?.insertBefore(container, fileElement.nextSibling);

    options.question.onPropertyChanged.add((question, opt) => {
      if (opt.name === 'value') {
        updatePreview({
          values: opt.newValue,
          container,
          onFileDelete,
          isEditMode,
        });
      }
    });
    updatePreview({
      values: options.question.value,
      container,
      onFileDelete,
      isEditMode,
    });
  }
}
